<template>
	<v-row>
		<v-col>

			<v-form v-model="formIsValid.details" ref="detailsForm">
				<ui-box
						:eager="!!product.id"
						:form="$refs.detailsForm"
						new-forms
				>

					<form-title title="Details">
						<template v-if="this.partner">
							Fill in the details for your quiz. Start by specifying the place where the
							quiz will be held, followed by giving your quiz a memorable name and a
							concise description that highlights the key themes and what participants can
							expect.
						</template>
						<template v-else>
							Fill in the details for your quiz. Begin with a memorable namethat
							represents your quiz, then include a brief description to summarize the
							main topics and participant experience.
						</template>
					</form-title>

					<v-row>
						<v-col v-if="this.partner" sm="6">
							<venue-select
								v-model="quizProduct.venue_id"
								:rules="rules.place"
								:venues="venues"
							></venue-select>
						</v-col>
						<v-col :sm="this.partner ? 6 : null">
							<v-text-field
									v-model="quizProduct.name"
									class="rounded-lg required"
									hide-details="auto"
									label="Name"
									outlined
									persistent-hint
									:rules="rules.name"
							></v-text-field>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<wysiwyg-input
									v-model="quizProduct.short_information"
									auto-grow
									class="rounded-lg required"
									:counter="1250"
									hide-details="auto"
									hint="In order to check in to this location,
											please answer this quiz question. You will have
											{number of opportunities/chances} to correctly
											answer the quiz question."
									label="Brief Description"
									outlined
									rows="4"
									:rules="rules.brief_description"
							></wysiwyg-input>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.quiz" ref="quizForm">
				<ui-box
						:eager="!!product.id"
						:form="$refs.quizForm"
						new-forms
				>

					<form-title title="Quiz">
						Craft your quiz question thoughtfully, providing clear options for
						participants to choose from. Decide whether they can select one or multiple
						answers, and specify which are correct.
					</form-title>

					<v-row>
						<v-col>
							<v-text-field
									v-model="quiz.question"
									class="rounded-lg required"
									counter
									label="Question"
									maxlength="256"
									outlined
									persistent-hint
									:rules="rules.question"
							></v-text-field>
						</v-col>
					</v-row>

					<form-divider inner/>

					<form-subsection title="Answer Key">
						You can have one or multiple correct answers. If there are multiple correct
						answers, participants must select all of them to get the question right.
					</form-subsection>

					<v-row>
						<v-col>
							<v-checkbox
									v-model="quiz.type"
									ref="multipleCorrectAnswers"
									false-value="single"
									hide-details="auto"
									label="This question has multiple correct answers"
									:rules="rules.multipleAnswers"
									true-value="multiple"
							></v-checkbox>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-radio-group
									v-model="correctAnswerId"
									:key="`${quiz.type}-${correctAnswerId}`"
									class="mt-0"
									hide-details="auto"
									:rules="rules.radio"
							>
								<ui-box no-padding>

									<v-container
											v-if="$vuetify.breakpoint.mdAndUp"
											style="line-height: 1rem;"
									>
										<v-row no-gutters
													 class="font-weight-bold grey--text mb-0 text-caption text--darken-2"
										>
											<v-col cols="auto" style="min-width: 30px;"></v-col>
											<v-col class="flex-grow-1">
												<v-row>
													<v-col class="flex-grow-1">Answer</v-col>
												</v-row>
											</v-col>
											<v-col
													class="d-flex justify-center"
													cols="auto"
													style="min-width: 70px;"
											>
												Correct
											</v-col>
											<v-col
													class="d-flex justify-end"
													cols="auto"
													style="min-width: 50px;"
											>
												Action
											</v-col>
										</v-row>
									</v-container>

									<v-divider
											v-if="$vuetify.breakpoint.mdAndUp"
											class="opacity-80"
									></v-divider>

									<v-container style="line-height: 1rem;">


										<draggable
												v-model="quiz.answers"
												class="my-n4 sortables"
												direction="vertical"
												filter="input"
												tag="div"
												@start="onDragStart"
												@end="onDragEnd"
										>
											<transition-group
													type="transition"
													:name="!drag ? 'flip-list' : null"
											>

												<v-row
														v-for="(answer, answerIdx) in quiz.answers"
														:key="`${quiz.type}-${answer.id}`"
														class="align-center ma-0"
														no-gutters
												>
													<v-col
															:class="{ 'pb-6': !isAnswerValid(answerIdx) }"
															cols="auto"
															style="min-width: 30px;"
													>
														<v-icon
																:style="{
																	marginTop: $vuetify.breakpoint.smAndDown ? 7 : 16
																}"
														>
															mdi-drag-vertical
														</v-icon>
													</v-col>
													<v-col class="flex-grow-1">
														<v-text-field
																v-model="answer.text"
																ref="quizAnswer"
																class="required rounded-lg"
																counter
																:dense="$vuetify.breakpoint.smAndDown"
																hide-details="auto"
																:label="`Quiz Answer #${ answerIdx + 1}`"
																maxlength="256"
																outlined
																persistent-hint
																type="text"
																:rules="rules.answer"
														></v-text-field>
													</v-col>
													<v-col
															class="d-flex justify-center"
															:class="{ 'pb-6': !isAnswerValid(answerIdx) }"
															cols="auto"
															style="min-width: 70px;"
													>
														<v-checkbox
																v-if="quiz.type === 'multiple'"
																v-model="answer.correct"
																class="ma-0"
																hide-details
																:rules="rules.checkbox"
														></v-checkbox>
														<v-radio
																v-if="quiz.type === 'single'"
																class="ma-0"
																hide-details
																:value="answer.id"
														></v-radio>
													</v-col>
													<v-col
															class="d-flex justify-end"
															:class="{ 'pb-6': !isAnswerValid(answerIdx) }"
															cols="auto"
															style="min-width: 50px;"
													>
														<v-btn
																color="error"
																depressed
																:disabled="quiz.answers.length <= 1"
																fab
																outlined
																small
																:style="{
																	marginTop: $vuetify.breakpoint.smAndDown ? 5 : 14
																}"
																@click="removeAnswer(answerIdx)"
														>
															<v-icon>
																mdi-trash-can-outline
															</v-icon>
														</v-btn>
													</v-col>
												</v-row>

											</transition-group>
										</draggable>

									</v-container>
								</ui-box>
							</v-radio-group>

						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-btn
									class="rounded-lg"
									color="info"
									depressed
									@click="addAnswer"
							>
								<v-icon v-if="quiz.type === 'multiple'">
									mdi-plus-box
								</v-icon>
								<v-icon v-else>
									mdi-plus-circle
								</v-icon>
								Add Answer
							</v-btn>
						</v-col>
					</v-row>

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.settings" ref="settingsForm">
				<ui-box
						:eager="!!product.id"
						:form="$refs.settingsForm"
						new-forms
				>

					<form-title title="Settings">
						Here you can set some of the finer functionality of the quiz like setting a
						confirmation message for correct answers, limiting the number of guesses, and
						control the timing between attempts to keep the quiz engaging and challenging.
					</form-title>

					<form-divider inner/>

					<form-subsection title="Success Message">
						Congratulate your participants with a simple message or further instruction.
					</form-subsection>

					<v-row>
						<v-col>
							<wysiwyg-input
									v-model="quiz.confirmation_message"
									:extensions="tiptapExtensions"
									label="Enter confirmation message here"
									:rules="rules.confirmation"
							></wysiwyg-input>
						</v-col>
					</v-row>

					<form-divider inner mt="8"/>

					<form-subsection title="Button Language">
						Customize the button language for taking the quiz and when the quiz is
						complete.
					</form-subsection>

					<v-row>
						<v-col md="8" lg="6">
							<v-row>
								<v-col>
									<v-text-field
										v-model="checkInLanguage"
										class="rounded-lg required"
										label="Take Quiz"
										outlined
										persistent-hint
										:rules="rules.redemption"
									></v-text-field>
								</v-col>
								<v-col>
									<v-text-field
										v-model="checkedInLanguage"
										class="rounded-lg required"
										label="Quiz Completed"
										outlined
										persistent-hint
										:rules="rules.mobile_text"
									></v-text-field>
								</v-col>
							</v-row>
						</v-col>
					</v-row>

					<form-divider inner mt="8"/>

					<form-subsection title="Retries & Cooldown">
						<ul class="grey--text text--darken-2 text-body-2">
							<li>
								Setting 1 guess means the user won’t be able to guess again or check in if
								they get it wrong.
							</li>
							<li>
								Setting more than 1 guess allows you to set a reset time for additional
								attempts.
							</li>
						</ul>
					</form-subsection>

					<v-row>
						<v-col cols="auto">
							<div class="d-flex guess-stepper">
								<v-btn
										class="px-0 rounded-lg rounded-r-0"
										:disabled="quiz.max_attempts === 1"
										x-large
										outlined
										@click="quiz.max_attempts--"
								>
									<v-icon class="ml-2" size="16">mdi-minus</v-icon>
								</v-btn>
								<v-chip
										class="rounded-0 justify-center px-0 text-h6"
										label
										outlined
										:ripple="false"
										style="
											background-color: #e0e0e0 !important;
											border-left: none;
											border-right: none;
										"
								>
									{{ quiz.max_attempts }}
								</v-chip>
								<v-btn
										class="px-0 rounded-lg rounded-l-0"
										x-large
										outlined
										@click="() => quiz.max_attempts++"
								>
									<v-icon class="ml-2" size="16">mdi-plus</v-icon>
								</v-btn>
							</div>
						</v-col>
						<template v-if="quiz.max_attempts > 1">
							<v-col md="8" lg="6">
								<v-row>
									<v-col sm="5">
										<v-select
												v-model="quiz.cool_down_type"
												class="rounded-lg required"
												label="Cooldown Unit"
												hide-details="auto"
												:items="durationTypes"
												item-text="name"
												outlined
												persistent-hint
												:rules="rules.repeat_delay_type"
										></v-select>
									</v-col>
									<v-col sm="7">
										<v-text-field
												v-model.number="quiz.cool_down"
												class="rounded-lg required"
												label="Cooldown Amount"
												hide-details="auto"
												hide-spin-buttons
												outlined
												persistent-hint
												ref="redemptionEndsAmount"
												:rules="rules.repeat_delay"
												:suffix="quiz.cool_down_type"
												type="number"
												@wheel.native.prevent
										></v-text-field>
									</v-col>
								</v-row>
							</v-col>
						</template>
					</v-row>

					<form-divider inner mt="8"/>
						<blackout-dates
								:refs="$refs"
								:product="product"
								:coupon="coupon"
								:form-is-valid="formIsValid"
								:timezone="partner?.timezone || timezone"
						/>					
					<form-divider inner mt="8"/>

					<form-subsection title="GPS Validation">
						Optional validation step to ensure the pass-holder is checking inwithin a
						certain radius of the Place.
					</form-subsection>

					<v-row>
						<v-col cols="auto">
							<v-checkbox
									v-model="secondaryValidation"
									class="mt-0"
									false-value=""
									hide-details
									label="Enable GPS Validation"
									true-value="gps"
									@change="$refs.radius?.validate()"
							></v-checkbox>
						</v-col>
					</v-row>

					<v-row>
						<v-col v-if="secondaryValidation" md="7" lg="6">
							<v-text-field
									v-model.number="quizProduct.redemption_radius"
									class="rounded-lg required"
									hide-spin-buttons
									hint="Minimum distance required from the place for product redemption."
									label="Mile Radius"
									outlined
									persistent-hint
									:rules="rules.radius"
									type="number"
									@wheel.native.prevent
							></v-text-field>
						</v-col>
					</v-row>

					

				</ui-box>

			</v-form>

			<mobile-pass-preview-modal
					v-if="preview.url"
					:preview-url="preview.url"
					@accept-preview="acceptPreview"
					@reject-preview="rejectPreview"
			></mobile-pass-preview-modal>

			<save-form
					:refs="$refs"
					:is-saving="isSaving"
					new-forms
					save-text="Save & Preview"
					@cancel-action="cancelChanges"
					@save-action="saveChanges"
			></save-form>
		</v-col>
	</v-row>
</template>

<script>
import {productHelpers, quizHelpers} from '../helpers';
import FormDivider from "../../../../UI/Form/FormDivider.vue";
import VenueSelect from '../Shared/VenueSelect.vue';
import BlackoutDates from "@app/Shared/Components/BlackoutDates.vue";
import ButtonLanguage from "../Shared/ButtonLanguage.vue";

export default {
	name: "QuizProductForm",
	components: {
		ButtonLanguage, 
		FormDivider,
		VenueSelect,
		BlackoutDates,
	},
	props: {
		template: {
			type: Object,
			default: () => ({})
		},
		venue: {
			type: Object,
			default: () => ({})
		},
		venues: {
			type: Array,
			default: () => []
		},
		partner: {
			type: Object | null,
			default: null
		},
		product: {
			type: Object,
			default: () => ({})
		},
		returnUrl: {
			type: String,
			default: ''
		},
		timezone: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			drag: false,
			durationTypes: [
				{ name: 'Days', value: 'days' },
				{ name: 'Hours', value: 'hours' },
				{ name: 'Minutes', value: 'minutes' },
				{ name: 'Seconds', value: 'seconds' }
			],
			formIsValid: {
				details: false,
				quiz: false,
				settings: false
			},
			isSaving: false,
			optionalValidations: [
				{
					name: 'None',
					value: ''
				},
				{
					name: 'Device GPS',
					value: 'gps'
				}
			],
			preview: {
				product: {},
				url: undefined
			},
			quizProduct: {
				...productHelpers.productFormat(this.product)
			},
			secondaryValidation: '',
			tiptapExtensions: [
				'Bold',
				'Underline',
				'Italic',
				'Strike',
				'Link',
				'HorizontalRule',
				'Undo',
				'Redo'
			],
		}
	},
	created() {
		const { settings: { quiz } } = this.quizProduct;
		this.$set(this.quizProduct.settings, 'quiz', new Question(quiz));
		if(this.quizProduct.secondary_redemption_validation) {
			this.secondaryValidation = 'gps';
		}
	},
	computed: {
		checkboxRulesError() {
			const { quiz: { answers, type } } = this;

			return type === 'multiple' && !answers.some(({ correct }) => correct)
					? 'At least one correct answer is required.'
					: '';
		},
		checkedInLanguage: {
			get() {
				return this.getLanguageTag('CHECKEDIN').value;
			},
			set(v) {
				const { quizProduct: { languageTags } } = this;
				const checkinIndex = this.getLanguageTagIndex('CHECKEDIN')
				languageTags[checkinIndex].value = v;
			}
		},
		checkInLanguage: {
			get() {
				return this.getLanguageTag('CHECKIN').value;
			},
			set(v) {
				const { quizProduct: { languageTags } } = this;
				const checkinIndex = this.getLanguageTagIndex('CHECKIN')
				languageTags[checkinIndex].value = v;
			}
		},
		correctAnswerId: {
			get() {
				const answer = this.quiz.answers.find(({ correct }) => !!correct);
				return answer?.id || '';
			},
			set(v) {
				if(this.quiz.type === 'multiple') return;

				this.quiz.answers.map(answer => {
					answer.correct = answer.id === v;
				});
			}
		},

		isAnswerValid() {
			return (answerIdx) => {
				return this.$refs.quizAnswer?.[answerIdx]?.valid === true;
			};
		},

		quiz() {
			return this.quizProduct?.settings?.quiz || {};
		},
		rules() {
			return quizHelpers.getValidationRules(this);
		}
	},
	methods: {
		acceptPreview() {
			productHelpers.acceptPreview(this.quizProduct, 'Quiz', this.preview, this.getReturnUrl());
		},

		addAnswer() {
			const { quiz: { answers } } = this;
			answers.push(new Answer({ correct: false, id: uuidV4(), text: '' }));

			this.$set(this.quiz, 'answers', answers);
			this.$nextTick(() => {
				this.$refs.quizAnswer[this.quiz.answers.length - 1].focus();
			});
		},
		cancelChanges() {
			window.location = this.getReturnUrl();
		},
		getLanguageTag(tag) {
			return this.quizProduct.languageTags?.find(
					({ tag: t }) => t === tag
			) || { value: '' };
		},
		getLanguageTagIndex(tag) {
			const { quizProduct: { languageTags } } = this;
			return languageTags.findIndex(
					({ tag: t }) => t === tag
			);
		},
		getReturnUrl() {
			return productHelpers.getReturnUrl(this.returnUrl, this.partner, this.venue);
		},

		onDragEnd() {
			this.drag = false;
		},

		onDragStart(event) {
			this.drag = true;
		},

		rejectPreview() {
			this.isSaving = false;
			this.preview.url = undefined;

			// Redirect to the edit page? Maybe not for now...
			// const currentPath = new URL(window.location.href).pathname;
			// const targetPath = new URL(`https://a.b.c${this.getEditUrl()}`).pathname;
			//
			// if(currentPath !== targetPath)
			// 	window.location.href = this.getEditUrl();
		},

		removeAnswer(index) {
			this.quiz.answers.splice(index, 1);
		},
		async saveChanges() {
			if(this.secondaryValidation === 'gps') {
				this.quizProduct.redemption_validation = 'gps';
				this.quizProduct.secondary_redemption_validation = 'quiz';
			}
			else {
				this.quizProduct.redemption_validation = 'quiz';
				this.quizProduct.secondary_redemption_validation = '';
			}

			productHelpers.saveChanges(this.isSaving, this.partner, this.venue, this.quizProduct, this.preview);
		},
		 
	},
	watch: {
		'quiz.answers': {
			deep: true,
			handler(newValue) {
				this.$nextTick(() => {
					this.$refs.multipleCorrectAnswers.validate();
				});
			}
		},
		'quiz.type': {
			handler(newValue) {
				this.$nextTick(async () => {
					// validate() does not work here so we much focus/blur each answer
					await this.quiz.answers.forEach((answer, idx) => {
						this.$refs.quizAnswer[idx].focus();
						this.$refs.quizAnswer[idx].blur();
					});
					// refocus the quiz type checkbox after forced validation
					this.$refs.multipleCorrectAnswers.$el.focus();
				});
			}
		}
	}
};

function Question(quiz = {}) {
	this.short_information = quiz.short_information || `In order to check in to this location, please answer this quiz question.`;
	this.confirmation_message = quiz.confirmation_message || 'Correct!';
	this.cool_down = parseInt(quiz.cool_down) || 0;
	this.cool_down_type = quiz.cool_down_type || '';
	this.correct_index = parseInt(quiz.correct_index) || 0;
	this.max_attempts = parseInt(quiz.max_attempts) || 1;
	this.redemption_after = quiz.redemption_after || '';
	this.redemption_before = quiz.redemption_before || '';
	this.question = quiz.question || '';
	this.type = quiz.type || 'single';
	this.answers = [];

	const { answers = [{ correct: true, id: uuidV4(), text: '' }] } = quiz;

	answers.map((answer) => {
		this.answers.push(new Answer(answer));
	});
}

function Answer(answer) {
	this.correct = answer.correct;
	this.text = answer.text;
	this.id = answer.id || uuidV4();
}

</script>

<style lang="scss" scoped>

.guess-stepper {
	& > * {
		border-color: #00000042 !important;
		height: 56px !important;
		min-width: unset !important;
		width: 56px !important;
	}
}

.sortables > span > div {
	margin: 0 -1rem !important;
	padding: 1rem;

	&:hover {
		background-color: #f0f8f0 !important;
	}

	&:not(:first-child):not(.sortable-drag) {
		border-top: solid 1px rgba(0, 0, 0, 0.096)
	}
}

.no-caret ::v-deep .v-input__append-inner {
	display: none;
}

</style>
